import { format } from 'date-fns';

export const formatDateTime = (date: Date) =>
  new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
  })
    .format(date)
    .toLowerCase();

export const formatTimeStamp = (timestamp: string) => formatDateTime(new Date(timestamp));

export const formatTime = (time: number): string => {
  const minutes = Math.floor(time / 60);
  const seconds = time % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

// choose only first and last name for the participant
export const getTruncatedName = (fullName: string) => {
  const nameParts = fullName.trim().split(' ');

  if (nameParts.length === 1) return nameParts[0];
  return `${nameParts[0]} ${nameParts[nameParts.length - 1]}`;
};

export const getRandomChar = (capitalLettersOnly: boolean = true): string => {
  const capitalLetters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const smallLetters = 'abcdefghijklmnopqrstuvwxyz';
  const chars = capitalLettersOnly ? capitalLetters : `${capitalLetters}${smallLetters}`;
  const randomIndex = Math.floor(Math.random() * chars.length);
  return chars[randomIndex];
};

export const generateRandomUUID = (capitalLettersOnly: boolean = true): string => {
  const segment1 = Array.from({ length: 3 }, () => getRandomChar(capitalLettersOnly)).join('');
  const segment2 = Array.from({ length: 4 }, () => getRandomChar(capitalLettersOnly)).join('');
  const segment3 = Array.from({ length: 3 }, () => getRandomChar(capitalLettersOnly)).join('');

  // Combine the segments with hyphens
  return `${segment1}-${segment2}-${segment3}`;
};

export const formatTimeRange = (start: Date, end: Date) => `${formatDateTime(start)} - ${formatDateTime(end)}`;

export const formatNumberToDigits = (time: number, digits: number = 2) => time.toString().padStart(digits, '0');

//capitalizes the first letter of each word in the given string and converts the rest of the letters to lowercase.
export const capitalize = (str: string) =>
  str
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');

// Format role for display in Invite Modal
// e.g., 'co_host' to 'Co_host'
export const fmtRole = (role: string) => {
  return role?.toLowerCase().replace(/^./, (c) => c.toUpperCase());
};

// Format CO_HOST role for display in notifications
// e.g., 'CO_HOST' to 'co-host'
export const formatCohostLabel = (role: string) => {
  return role?.toLowerCase().replace(/_/g, '-');
};

export function toKFormat(num: number) {
  if (num <= 999) return num.toString();
  return (num / 1000).toFixed(2) + 'K';
}

export const formatDateWithCity = (date: Date, dateFormat = 'EEE, dd MMM, yyyy hh:mm a') => {
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const offsetMinutes = -new Date().getTimezoneOffset();
  const offsetHours = offsetMinutes / 60;
  const gmtString = `GMT${offsetHours >= 0 ? `+${offsetHours}` : offsetHours}`;

  return `${format(date, dateFormat)} ${timeZone} ${gmtString}`;
};

// Helper function to create a clean filename by removing spaces and special characters
export const createCleanFileName = (baseName: string, dateString: string, timeString: string) => {
  // Remove spaces and special characters, replace with underscores
  const cleanBaseName = baseName.replace(/[^a-zA-Z0-9]/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '');
  return `${cleanBaseName}_${dateString}_${timeString}.png`;
};

// Create a standardized screenshot filename with the format {baseName}_YYYY-MM-DD_HH-MM.png
export const createScreenshotFileName = (baseName?: string) => {
  const now = new Date();
  const dateString = `${now.getFullYear()}-${(now.getMonth() + 1).toString().padStart(2, '0')}-${now.getDate().toString().padStart(2, '0')}`;
  const timeString = `${now.getHours().toString().padStart(2, '0')}-${now.getMinutes().toString().padStart(2, '0')}`;
  
  // Use provided base name or default to 'screenshot'
  const name = baseName || 'screenshot';
  
  return createCleanFileName(name, dateString, timeString);
};
